/**
 * Shared open/close behaviour for the shell's click-driven panels — nav
 * flyouts, the workspace switcher menu, the account menu. Per
 * assets/handoffs/design_handoff_app_shell: click to open, Escape and
 * outside-click to close, focus returns to the trigger. Never hover.
 *
 * Positioning is deliberately left to each caller — a flyout opens right of
 * the rail, the workspace menu opens down, the account menu opens up, and
 * forcing one geometry helper onto all three would fight more than it'd save.
 *
 * @returns {{open, setOpen, triggerRef, panelRef, close}}
 */
import { useEffect, useRef, useState } from "react";

export const useDismissablePanel = () => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const close = (returnFocus = true) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  useEffect(() => {
    if (!open) return;

    const handleClick = (e) => {
      if (
        triggerRef.current?.contains(e.target) ||
        panelRef.current?.contains(e.target)
      ) {
        return;
      }
      close(false);
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") close();
    };

    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return { open, setOpen, triggerRef, panelRef, close };
};
